import type { ApiError } from '../types/api';
import { isDemoMode, maybeMock } from './mocks';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export class HttpError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  if (isDemoMode) {
    const mocked = await maybeMock<T>(opts.method ?? 'GET', path, opts.body);
    if (mocked !== undefined) return mocked;
    // Fall through to real fetch if a path isn't mocked
  }
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    ...(opts.headers ?? {}),
  };
  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (response.status === 204) return undefined as T;
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    const e = (data as ApiError | null)?.error;
    throw new HttpError(
      response.status,
      e?.code ?? 'unknown_error',
      e?.message ?? `Request failed: ${response.status}`,
      e?.details,
    );
  }
  return data as T;
}

/** SSE subscription. Returns a cleanup function. */
export function subscribeSSE(
  path: string,
  handlers: { onMessage?: (event: MessageEvent) => void; onError?: (err: Event) => void; onOpen?: () => void },
  events: string[] = ['message', 'status'],
): () => void {
  if (isDemoMode) {
    // Synthesize a few status events so the StatusDisplay isn't empty in demos
    const ticks = [
      'On the case…',
      'Asking QuickBooks nicely…',
      'Charting your findings…',
    ];
    const timers: ReturnType<typeof setTimeout>[] = [];
    ticks.forEach((text, i) => {
      timers.push(
        setTimeout(() => {
          handlers.onMessage?.(
            new MessageEvent('status', { data: JSON.stringify({ text, count: i + 1 }) }),
          );
        }, 200 + i * 1200),
      );
    });
    return () => timers.forEach(clearTimeout);
  }
  const url = `${API_BASE}${path}`;
  const source = new EventSource(url, { withCredentials: true });
  if (handlers.onOpen) source.onopen = handlers.onOpen;
  if (handlers.onError) source.onerror = handlers.onError;
  if (handlers.onMessage) {
    for (const evt of events) {
      source.addEventListener(evt, handlers.onMessage as EventListener);
    }
  }
  return () => source.close();
}
